import { css, styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

type PaymentMethodTagVariant = 'default' | 'selected'

const MESSAGE_ID: Record<PaymentMethodTagVariant, string> = {
  default: 'PaymentMethodCard.default',
  selected: 'PaymentMethodCard.selected',
}

const TEST_ID: Record<PaymentMethodTagVariant, string> = {
  default: 'PaymentMethodDefaultTag',
  selected: 'PaymentMethodSelectedTag',
}

// Keyed by variant so adding a member to PaymentMethodTagVariant fails to compile
// until its styling is defined here — no silent fallthrough to another variant.
const VARIANT_STYLES: Record<
  PaymentMethodTagVariant,
  (backgroundColor?: string) => ReturnType<typeof css>
> = {
  default: (backgroundColor) => css`
    background-color: ${backgroundColor ?? Colors.Semantic.Neutral.light_grey};
    color: ${Colors.Semantic.Neutral.very_dark_grey};
  `,
  selected: () => css`
    background-color: ${Colors.Semantic.SpecificFunctions.Tags.success};
    color: ${Colors.Semantic.Neutral.very_dark_grey};
  `,
}

const Tag = styled.div<{
  $variant: PaymentMethodTagVariant
  $backgroundColor?: string
}>`
  ${Typography.Body2}
  display: inline-block;
  border-radius: 0.25rem;
  padding: 0.1875rem 0.5rem;
  font-weight: bold;

  ${({ $variant, $backgroundColor }) =>
    VARIANT_STYLES[$variant]($backgroundColor)}
`

/**
 * Small badge shown next to a payment method: `default` = the account primary
 * method, `selected` = the buyer's autopay pick for this listing/contract.
 * `backgroundColor` overrides the default variant's grey (e.g. white when the
 * badge is embedded on a grey card); it is ignored for the selected variant.
 */
export const PaymentMethodTag = ({
  variant,
  backgroundColor,
}: {
  variant: PaymentMethodTagVariant
  backgroundColor?: string
}) => (
  <Tag
    data-testid={TEST_ID[variant]}
    $variant={variant}
    $backgroundColor={backgroundColor}
  >
    <FormattedMessage id={MESSAGE_ID[variant]} />
  </Tag>
)
